/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { renderDefaultError, SmartComponentProps } from '@exo/frontend-common-utils';
import { gql } from '@apollo/client';
import { useProduct } from '../../hooks/useProduct';
import { ProductRef } from '../../model/product-ref';
import { useCategoriesTop } from '../../hooks/useCategoriesTop';
import {
  GIFT_ACCESSORIES_IDENTIFIER,
  GIFTCARD_IDENTIFIER,
  GIFTVOUCHER_IDENTIFIER
} from '../../../catalogConfig';
import _ from 'lodash';
import { useSessionContext } from '@exo/frontend-common-session-context';

export const ProductTypeContainer = ({
  productId,
  render,
  renderLoading = () => null,
  facetState,
  renderError = renderDefaultError
}: Props) => {
  const {
    loading: loadingCategoriesTop,
    data: categoriesTopData,
    error: categoriesTopError
  } = useCategoriesTop();

  const sessionStorage = useSessionContext();
  const { loading, data, error } = useProduct<ProductTypeResponse>(
    { productId },
    ProductTypeContainer.fragment
  );
  const parentId = data?.product?.parent?.parentCategory?.id;
  const sessionCats = (sessionStorage?.cartCategories && sessionStorage?.cartCategories) || [];
  const uniqueCats: string[] = (parentId && _.uniq([...sessionCats, parentId])) || sessionCats;

  !_.isEqual(sessionCats, uniqueCats) && sessionStorage?.set({ cartCategories: [...uniqueCats] });

  if (loading || loadingCategoriesTop) return renderLoading();
  if (error || categoriesTopError) return renderError(error);
  const getAllGiftItems = categoriesTopData?.categoriesTop?.filter(
    eachItem => eachItem?.identifier === GIFT_ACCESSORIES_IDENTIFIER
  )[0]?.childCategories;
  // console.log('getAllGiftItems', getAllGiftItems);
  const giftCards = getAllGiftItems?.filter(
    eachGiftItem => eachGiftItem?.identifier === GIFTCARD_IDENTIFIER
  );
  const giftVouchers = getAllGiftItems?.filter(
    eachGiftItem => eachGiftItem?.identifier === GIFTVOUCHER_IDENTIFIER
  );
  // console.log('giftVouchers', giftVouchers);
  const giftCardsId = giftCards?.length > 0 ? giftCards[0]?.id : '';
  const giftVouchersId = giftVouchers?.length > 0 ? giftVouchers[0]?.id : '';
  const hasGiftCard = (Id, giftCardId) => {
    return Id === giftCardId ? true : false;
  };
  facetState?.setIsGiftCard(hasGiftCard(parentId, giftCardsId));
  const hasGiftVoucher = (Id, giftVoucherId) => {
    return Id === giftVoucherId ? true : false;
  };

  let barcodeAttr = data?.product?.attributes?.filter(item => {
    return item.name === 'barcode';
  });

  let altRefName = data?.product?.attributes?.filter(item => {
    return item.name === 'altRef2';
  });

  let variantdescriptionName = data?.product?.attributes?.filter(item => {
    return item.name === 'variantdescription';
  });

  return render({
    productType: data?.product?.type!,
    parentProductType: data?.product?.parent?.type,
    isGiftCard: hasGiftCard(parentId, giftCardsId),
    isGiftVoucher: hasGiftVoucher(parentId, giftVouchersId),
    hasPrice: !!data?.product?.price?.offer || !!data?.product?.price?.list,
    barcode: barcodeAttr?.length ? barcodeAttr[0]?.value?.value : '',
    altRef2: altRefName?.length ? altRefName[0]?.value?.value : '',

    variantdescription: variantdescriptionName?.length
      ? variantdescriptionName[0]?.value?.value
      : ''
  });
};

ProductTypeContainer.fragment = gql`
  fragment ProductTypeContainer on PrdItem {
    id
    type
    attributes {
      id
      name
      value {
        value
        id
      }
    }
    parent {
      type
      id
      name
      parentCategory {
        id
        name
      }
    }
    price {
      list {
        value
      }
      offer {
        value
      }
    }
  }
`;

type ProductTypeResponse = {
  id: string;
  type: 'product' | 'sku' | 'bundleOfSkus' | 'bundleOfProducts';
  parent?: {
    type: 'product' | 'sku' | 'bundleOfSkus' | 'bundleOfProducts';
    parentCategory: {
      id: string;
    };
  };
  price?: {
    list?: any;
    offer?: any;
  };
  attributes?: {
    id: string;
    name: string;
    value: {
      value: string;
      id: string;
    };
  }[];
};

type Props = SmartComponentProps<{
  productId: ProductRef;
  facetState?: any;
  render: (props: ProductTypeContainerRenderProps) => JSX.Element;
}>;

type ProductTypeContainerRenderProps = {
  productType: 'product' | 'sku' | 'bundleOfSkus' | 'bundleOfProducts';
  parentProductType?: 'product' | 'sku' | 'bundleOfSkus' | 'bundleOfProducts';
  hasPrice: boolean;
  isGiftCard: boolean;
  isGiftVoucher: boolean;
  barcode?: string;
  altRef2?: string;
  variantdescription?: string;
};
